import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const navLinks = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Coverage', href: '#coverage' },
  { label: 'Roadmap', href: '#roadmap' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Contact', href: '#contact' },
];

export const Navbar = () => {
  const [activeSection, setActiveSection] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' }
    );

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-200 ${
        scrolled ? 'shadow-sm' : ''
      }`}
      style={{ background: '#FFFFFF', borderBottom: '1px solid #E7E7EF' }}
    >
      <div className="container-main flex items-center justify-between h-16">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/logo-icon.png" alt="TexSolution" className="h-8 w-auto" style={{ objectFit: 'contain' }} />
          <span className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0B0B10' }}>
            Tex<span style={{ color: '#7C3AED' }}>Solution</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <button
              key={link.href}
              data-testid={`nav-link-${link.href.replace('#', '')}`}
              onClick={() => scrollToSection(link.href)}
              className={`text-sm font-medium transition-colors duration-200 hover:text-[#7C3AED] pb-1 ${
                activeSection === link.href.replace('#', '')
                  ? 'text-[#7C3AED] nav-active'
                  : 'text-[#2A2A36]'
              }`}
              style={{ fontFamily: 'Space Grotesk, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          <Button
            data-testid="nav-cta"
            onClick={() => scrollToSection('#contact')}
            className="bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl h-10 px-5 text-sm btn-press"
          >
            Get early access
          </Button>
        </div>

        {/* Mobile menu */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-white">
            <div className="flex flex-col gap-4 mt-8">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="text-left text-base font-medium text-[#2A2A36] hover:text-[#7C3AED] transition-colors py-2"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  {link.label}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection('#contact')}
                className="mt-4 bg-[#7C3AED] text-white hover:bg-[#6D28D9] rounded-xl h-11 btn-press"
              >
                Get early access
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
